import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Plus, ShieldCheck, FilePdf, MicrosoftWordLogo, Lightning, PencilSimple, AirplaneTilt, Anchor,
  CaretDown, CaretUp, PaperPlaneTilt, EnvelopeSimple, ChatText, Copy, LinkSimple, NotePencil, Lock, ArrowCounterClockwise, X,
} from '@phosphor-icons/react';
import {
  CloseDialog, DuplicateDialog, LinkDialog, NoteDialog, RecordResponseDialog, RequestResponseDialog,
} from '../components/safety/RecommendationDialogs';

const API = '/api/safety-recommendations';

const CLASSIFICATIONS = [{ value: 'urgent', label: 'Urgent' }, { value: 'routine', label: 'Routine' }];
const PRIORITIES = [
  { value: 'high', label: 'High', color: 'bg-red-100 text-red-800' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-100 text-amber-800' },
  { value: 'low', label: 'Low', color: 'bg-green-100 text-green-800' },
];
const STATUS_STYLE = {
  draft: ['Draft', 'bg-slate-100 text-slate-700'],
  open: ['Open', 'bg-blue-100 text-blue-800'],
  closed: ['Closed', 'bg-emerald-100 text-emerald-800'],
};
const RESPONSE_STYLE = {
  awaiting: ['Awaiting response', 'bg-amber-50 text-amber-800 border border-amber-200'],
  overdue: ['Response overdue', 'bg-red-50 text-red-800 border border-red-200'],
  received: ['Response received', 'bg-emerald-50 text-emerald-800 border border-emerald-200'],
};
const FILTERS = [
  { key: 'all', label: 'All' }, { key: 'draft', label: 'Draft' }, { key: 'open', label: 'Open' },
  { key: 'overdue', label: 'Overdue' }, { key: 'closed', label: 'Closed' },
];

const emptyForm = {
  investigation_id: '', addressee: '', addressee_organisation: '', addressee_email: '', addressee_state: '',
  classification: 'routine', safety_deficiency: '', factual_information: '', probable_cause_link: '',
  recommended_action: '', expected_outcome: '', response_deadline: '', priority: 'medium', recommendation: '',
};

const labelFor = (list, value) => list?.find((o) => o.value === value)?.label || '';

function Detail({ label, children }) {
  return <div><span className="text-xs text-slate-500 block">{label}</span><span className="font-medium text-slate-800 break-words">{children || '-'}</span></div>;
}

function RecCard({ rec, options, invNumbers, canEdit, onAction, onExport }) {
  const [expanded, setExpanded] = useState(false);
  const [statusLabel, statusStyle] = STATUS_STYLE[rec.status] || STATUS_STYLE.open;
  const response = RESPONSE_STYLE[rec.response_status];
  const priorityStyle = PRIORITIES.find((p) => p.value === rec.priority)?.color || 'bg-slate-100 text-slate-700';
  const isOpen = rec.status === 'open';

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow" data-testid={`rec-card-${rec.id}`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between px-5 py-4 text-left" data-testid={`toggle-rec-${rec.id}`}>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0"><ShieldCheck size={18} className="text-amber-600" /></div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm font-bold text-slate-800">{rec.rec_number}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusStyle}`}>{statusLabel}</span>
              {response && isOpen && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${response[1]}`}>{response[0]}</span>}
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityStyle}`}>{rec.priority}</span>
              {rec.classification === 'urgent' && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-600 text-white">URGENT</span>}
            </div>
            <p className="text-sm text-slate-600 truncate mt-0.5">
              <span className="text-slate-400">To {rec.addressee || 'addressee not set'}: </span>{rec.recommended_action || rec.recommendation || 'No action specified'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-slate-400 hidden sm:inline">{rec.investigation_number}</span>
          {expanded ? <CaretUp size={16} className="text-slate-400" /> : <CaretDown size={16} className="text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-100 px-5 py-4 space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <Detail label="Addressee">{rec.addressee}</Detail>
            <Detail label="Organisation / State">{[rec.addressee_organisation, rec.addressee_state].filter(Boolean).join(', ')}</Detail>
            <Detail label="Email">{rec.addressee_email}</Detail>
            <Detail label="Classification"><span className="capitalize">{rec.classification}</span></Detail>
            <Detail label="Issued">{rec.issued_at ? new Date(rec.issued_at).toLocaleDateString() : 'Not yet issued'}</Detail>
            <Detail label="Response due">{rec.response_deadline}</Detail>
            <Detail label="Investigations">{(rec.linked_investigation_ids || []).map((id) => invNumbers[id] || rec.investigation_number).join(', ')}</Detail>
            {rec.duplicated_from && <Detail label="Copied from">{rec.duplicated_from}</Detail>}
          </div>

          {[['Safety Deficiency', rec.safety_deficiency], ['Factual Information', rec.factual_information],
            ['Probable Cause Link', rec.probable_cause_link], ['Recommended Action', rec.recommended_action || rec.recommendation],
            ['Expected Outcome', rec.expected_outcome]].map(([label, value]) => (value ? (
            <div key={label}>
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">{label}</h4>
              <p className="text-sm text-slate-800 whitespace-pre-wrap">{value}</p>
            </div>
          ) : null))}

          <div>
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Responses</h4>
            {rec.responses?.length ? (
              <ol className="space-y-3" data-testid={`responses-${rec.id}`}>
                {rec.responses.map((r) => (
                  <li key={r.id} className="border border-slate-200 rounded-lg p-3 text-sm">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                      <span className="font-semibold text-slate-700">Round {r.round}</span>
                      {r.requested_at && <span>Requested {r.requested_at.slice(0, 10)} by {r.request_channel}</span>}
                      {r.due_date && <span>Due {r.due_date}</span>}
                      {r.received_at ? <span className="text-emerald-700">Received {r.received_at}{r.responder ? ` from ${r.responder}` : ''}</span>
                        : <span className="text-amber-700">Awaiting reply</span>}
                    </div>
                    {r.request_message && <p className="text-slate-600 mt-2 italic">“{r.request_message}”</p>}
                    {r.response_text && (
                      <div className="mt-2">
                        <span className="text-xs font-medium text-slate-700">{labelFor(options.response_types, r.response_type)}</span>
                        <p className="text-slate-800 whitespace-pre-wrap mt-1">{r.response_text}</p>
                      </div>
                    )}
                    {r.assessment && (
                      <p className="mt-2 text-xs"><span className="font-semibold text-slate-700">Assessment: </span>{labelFor(options.assessments, r.assessment)}
                        {r.assessment_comment && <span className="text-slate-600"> - {r.assessment_comment}</span>}</p>
                    )}
                  </li>
                ))}
              </ol>
            ) : <p className="text-sm text-slate-400">{rec.status === 'draft' ? 'Issue the recommendation to start requesting responses.' : 'No response requested yet.'}</p>}
          </div>

          {rec.closure && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
              <span className="font-semibold text-emerald-900">Closed {rec.closure.closed_at?.slice(0, 10)}</span>
              {rec.closure.final_assessment && <span className="text-emerald-900"> - {labelFor(options.assessments, rec.closure.final_assessment)}</span>}
              <p className="text-emerald-900 mt-1 whitespace-pre-wrap">{rec.closure.reason}</p>
            </div>
          )}

          {canEdit && rec.notes?.length > 0 && (
            <div>
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1"><Lock size={12} />Internal notes</h4>
              <ul className="space-y-2">
                {rec.notes.map((n) => (
                  <li key={n.id} className="bg-slate-50 rounded-lg p-3 text-sm">
                    <div className="text-xs text-slate-500">{n.at?.slice(0, 10)} - {n.by}{n.subject && <b className="text-slate-700"> - {n.subject}</b>}</div>
                    <p className="text-slate-800 whitespace-pre-wrap mt-1">{n.text}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100">
            {canEdit && rec.status === 'draft' && (
              <Button size="sm" className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white" onClick={() => onAction('issue', rec)} data-testid={`issue-rec-${rec.id}`}>
                <PaperPlaneTilt size={14} className="mr-1" />Issue
              </Button>
            )}
            {canEdit && isOpen && (
              <>
                <Button size="sm" variant="outline" className="rounded-lg" onClick={() => onAction('request', rec)} data-testid={`request-rec-${rec.id}`}><EnvelopeSimple size={14} className="mr-1" />Request response</Button>
                <Button size="sm" variant="outline" className="rounded-lg" onClick={() => onAction('respond', rec)} data-testid={`respond-rec-${rec.id}`}><ChatText size={14} className="mr-1" />Record response</Button>
              </>
            )}
            {canEdit && rec.status !== 'closed' && (
              <Button size="sm" variant="outline" className="rounded-lg" onClick={() => onAction('edit', rec)} data-testid={`edit-rec-${rec.id}`}><PencilSimple size={14} className="mr-1" />Edit</Button>
            )}
            {canEdit && (
              <>
                <Button size="sm" variant="outline" className="rounded-lg" onClick={() => onAction('duplicate', rec)}><Copy size={14} className="mr-1" />Another addressee</Button>
                <Button size="sm" variant="outline" className="rounded-lg" onClick={() => onAction('link', rec)}><LinkSimple size={14} className="mr-1" />Link investigation</Button>
                <Button size="sm" variant="outline" className="rounded-lg" onClick={() => onAction('note', rec)}><NotePencil size={14} className="mr-1" />Note</Button>
              </>
            )}
            {canEdit && isOpen && (
              <Button size="sm" variant="outline" className="rounded-lg text-emerald-700 border-emerald-200" onClick={() => onAction('close', rec)} data-testid={`close-rec-${rec.id}`}><X size={14} className="mr-1" />Close</Button>
            )}
            {canEdit && rec.status === 'closed' && (
              <Button size="sm" variant="outline" className="rounded-lg" onClick={() => onAction('reopen', rec)}><ArrowCounterClockwise size={14} className="mr-1" />Reopen</Button>
            )}
            <Button variant="outline" size="sm" className="rounded-lg text-red-600 border-red-200 hover:bg-red-50" onClick={() => onExport(rec, 'pdf')} data-testid={`pdf-rec-${rec.id}`}><FilePdf size={14} className="mr-1" />PDF</Button>
            <Button variant="outline" size="sm" className="rounded-lg text-blue-600 border-blue-200 hover:bg-blue-50" onClick={() => onExport(rec, 'docx')} data-testid={`docx-rec-${rec.id}`}><MicrosoftWordLogo size={14} className="mr-1" />Word</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SafetyRecommendations() {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'iic';
  const [recommendations, setRecommendations] = useState([]);
  const [investigations, setInvestigations] = useState([]);
  const [options, setOptions] = useState({ response_types: [], assessments: [], copyable_fields: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [autoFilling, setAutoFilling] = useState(false);
  const [dialog, setDialog] = useState({ kind: null, rec: null });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const loadData = async () => {
    try {
      const [recRes, invRes, optRes] = await Promise.all([
        axios.get(API, { withCredentials: true }),
        axios.get('/api/investigations', { withCredentials: true }),
        axios.get(`${API}/options`, { withCredentials: true }),
      ]);
      setRecommendations(recRes.data);
      setInvestigations(invRes.data);
      setOptions(optRes.data);
    } catch (error) {
      setMessage({ ok: false, text: 'Could not load safety recommendations' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const invNumbers = useMemo(() => Object.fromEntries(investigations.map((i) => [i.id, i.investigation_number])), [investigations]);
  const counts = useMemo(() => ({
    all: recommendations.length,
    draft: recommendations.filter((r) => r.status === 'draft').length,
    open: recommendations.filter((r) => r.status === 'open').length,
    overdue: recommendations.filter((r) => r.status === 'open' && r.response_status === 'overdue').length,
    closed: recommendations.filter((r) => r.status === 'closed').length,
  }), [recommendations]);
  const visible = recommendations.filter((r) => (filter === 'all' ? true
    : filter === 'overdue' ? r.status === 'open' && r.response_status === 'overdue' : r.status === filter));

  const replace = (updated) => setRecommendations((list) => {
    const exists = list.some((r) => r.id === updated.id);
    return exists ? list.map((r) => (r.id === updated.id ? updated : r)) : [updated, ...list];
  });

  const run = async (fn, okText) => {
    setBusy(true); setMessage(null);
    try {
      const res = await fn();
      const rec = res.data?.recommendation || res.data;
      if (rec?.id) replace(rec);
      setDialog({ kind: null, rec: null });
      if (okText) setMessage({ ok: true, text: okText });
      return true;
    } catch (err) {
      setMessage({ ok: false, text: formatApiErrorDetail(err.response?.data?.detail) });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleAutoFill = async (invId) => {
    if (!invId) return;
    setAutoFilling(true);
    try {
      const { data } = await axios.get(`${API}/auto-fill/${invId}`, { withCredentials: true });
      setFormData((prev) => ({ ...prev, investigation_id: invId,
        factual_information: data.factual_information || prev.factual_information,
        probable_cause_link: data.probable_cause_link || prev.probable_cause_link }));
    } catch (e) {
      setMessage({ ok: false, text: 'Auto-fill failed' });
    } finally {
      setAutoFilling(false);
    }
  };

  const save = async (issue) => {
    const payload = { ...formData };
    const ok = await run(async () => {
      if (editingId) return axios.put(`${API}/${editingId}`, payload, { withCredentials: true });
      return axios.post(API, { ...payload, status: issue ? 'open' : 'draft' }, { withCredentials: true });
    }, editingId ? 'Recommendation updated' : (issue ? 'Recommendation issued' : 'Draft saved'));
    if (ok) { setIsFormOpen(false); setEditingId(null); setFormData({ ...emptyForm }); }
  };

  const onAction = (kind, rec) => {
    if (kind === 'edit') {
      setEditingId(rec.id);
      setFormData(Object.fromEntries(Object.keys(emptyForm).map((k) => [k, rec[k] ?? emptyForm[k]])));
      setIsFormOpen(true);
    } else if (kind === 'issue') {
      if (window.confirm(`Issue ${rec.rec_number} to ${rec.addressee}? The response period starts today.`)) {
        run(() => axios.post(`${API}/${rec.id}/issue`, {}, { withCredentials: true }), 'Recommendation issued');
      }
    } else if (kind === 'reopen') {
      run(() => axios.post(`${API}/${rec.id}/reopen`, {}, { withCredentials: true }), 'Recommendation reopened');
    } else {
      setDialog({ kind, rec });
    }
  };

  const post = (path, body, okText) => run(() => axios.post(`${API}/${dialog.rec.id}/${path}`, body, { withCredentials: true }), okText);

  const handleExport = async (rec, format) => {
    try {
      const res = await axios.get(`${API}/${rec.id}/${format}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${rec.rec_number}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setMessage({ ok: false, text: 'Export failed' });
    }
  };

  const updateField = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));
  const closeDialog = () => setDialog({ kind: null, rec: null });

  return (
    <div className="max-w-5xl" data-testid="safety-recommendations-page">
      <div className="flex items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Safety Recommendations</h1>
          <p className="text-sm text-slate-500 mt-1">ICAO Annex 13 (6.8 to 6.12): one addressee per recommendation, responses requested within 90 days</p>
        </div>
        {canEdit && (
          <Button className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg shrink-0" onClick={() => { setEditingId(null); setFormData({ ...emptyForm }); setIsFormOpen(true); }} data-testid="new-recommendation-btn">
            <Plus size={16} className="mr-2" /> New Recommendation
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="rec-filters">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
            className={`px-4 py-2 rounded-xl text-sm font-medium border ${filter === f.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}>
            {f.label} <span className={`ml-1 ${f.key === 'overdue' && counts.overdue ? 'text-red-500' : 'opacity-60'}`}>{counts[f.key]}</span>
          </button>
        ))}
      </div>

      {message && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm border ${message.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`}>{message.text}</div>
      )}

      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-xl p-12 text-center text-slate-500 border border-slate-200">Loading...</div>
        ) : visible.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border border-slate-200">
            <ShieldCheck size={40} className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No safety recommendations here</p>
          </div>
        ) : visible.map((rec) => (
          <RecCard key={rec.id} rec={rec} options={options} invNumbers={invNumbers} canEdit={canEdit} onAction={onAction} onExport={handleExport} />
        ))}
      </div>

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>{editingId ? 'Edit Safety Recommendation' : 'New Safety Recommendation'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); save(false); }} className="space-y-4" data-testid="recommendation-form">
            {!editingId && (
              <div>
                <Label className="text-xs font-bold text-slate-700 uppercase">Investigation *</Label>
                <div className="flex gap-2 mt-1">
                  <div className="flex-1">
                    <Select value={formData.investigation_id} onValueChange={(val) => { updateField('investigation_id', val); handleAutoFill(val); }}>
                      <SelectTrigger className="rounded-lg" data-testid="form-investigation-select"><SelectValue placeholder="Select investigation..." /></SelectTrigger>
                      <SelectContent>
                        {investigations.map((inv) => (
                          <SelectItem key={inv.id} value={inv.id}>
                            <span className="flex items-center gap-1">{inv.sector === 'aviation' ? <AirplaneTilt size={12} /> : <Anchor size={12} />}{inv.investigation_number} - {inv.title}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.investigation_id && (
                    <Button type="button" variant="outline" size="sm" className="rounded-lg shrink-0" onClick={() => handleAutoFill(formData.investigation_id)} disabled={autoFilling} data-testid="auto-fill-btn">
                      <Lightning size={14} className="mr-1" />{autoFilling ? 'Filling...' : 'Auto-fill'}
                    </Button>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs font-bold text-slate-700 uppercase">Addressee *</Label>
                <Input value={formData.addressee} onChange={(e) => updateField('addressee', e.target.value)} className="mt-1 rounded-lg" placeholder="e.g. Director General of Civil Aviation" data-testid="form-addressee" required />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs font-bold text-slate-700 uppercase">Organisation</Label>
                <Input value={formData.addressee_organisation} onChange={(e) => updateField('addressee_organisation', e.target.value)} className="mt-1 rounded-lg" placeholder="e.g. Civil Aviation Authority" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs font-bold text-slate-700 uppercase">Addressee Email</Label>
                <Input type="email" value={formData.addressee_email} onChange={(e) => updateField('addressee_email', e.target.value)} className="mt-1 rounded-lg" placeholder="For response requests" data-testid="form-addressee-email" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs font-bold text-slate-700 uppercase">Addressee State</Label>
                <Input value={formData.addressee_state} onChange={(e) => updateField('addressee_state', e.target.value)} className="mt-1 rounded-lg" />
              </div>
              <div>
                <Label className="text-xs font-bold text-slate-700 uppercase">Classification</Label>
                <Select value={formData.classification} onValueChange={(val) => updateField('classification', val)}>
                  <SelectTrigger className="mt-1 rounded-lg" data-testid="form-classification"><SelectValue /></SelectTrigger>
                  <SelectContent>{CLASSIFICATIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold text-slate-700 uppercase">Priority</Label>
                <Select value={formData.priority} onValueChange={(val) => updateField('priority', val)}>
                  <SelectTrigger className="mt-1 rounded-lg" data-testid="form-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-bold text-slate-700 uppercase">Response Due By</Label>
                <Input type="date" value={formData.response_deadline} onChange={(e) => updateField('response_deadline', e.target.value)} className="mt-1 rounded-lg" data-testid="form-deadline" />
                <p className="text-xs text-slate-500 mt-1">Leave empty to set it automatically to 90 days after issue.</p>
              </div>
            </div>

            {[['safety_deficiency', 'Safety Deficiency Identified *', 80, true, 'Describe the safety deficiency...'],
              ['factual_information', 'Supporting Factual Information', 80, false, 'Auto-filled from investigation data...'],
              ['probable_cause_link', 'Link to Probable Cause', 60, false, 'Auto-filled from synthesis...'],
              ['recommended_action', 'Recommended Action *', 100, true, 'It is recommended that...'],
              ['expected_outcome', 'Expected Outcome', 60, false, 'What outcome is expected if the recommendation is implemented?'],
            ].map(([key, label, height, required, placeholder]) => (
              <div key={key}>
                <Label className="text-xs font-bold text-slate-700 uppercase">{label}</Label>
                <Textarea value={formData[key]} onChange={(e) => updateField(key, e.target.value)} className="mt-1 rounded-lg" style={{ minHeight: height }}
                  placeholder={placeholder} required={required} data-testid={`form-${key}`} />
              </div>
            ))}

            <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-100">
              <Button type="submit" disabled={busy} variant={editingId ? 'default' : 'outline'} className={`rounded-lg ${editingId ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white' : ''}`} data-testid="submit-recommendation">
                {editingId ? 'Save changes' : 'Save as draft'}
              </Button>
              {!editingId && (
                <Button type="button" disabled={busy} className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg" data-testid="issue-recommendation"
                  onClick={(e) => { const form = e.currentTarget.form; if (form.reportValidity()) save(true); }}>
                  <PaperPlaneTilt size={14} className="mr-1" />Save and issue
                </Button>
              )}
              <Button type="button" variant="outline" className="rounded-lg" onClick={() => setIsFormOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <RequestResponseDialog rec={dialog.kind === 'request' ? dialog.rec : null} busy={busy} onClose={closeDialog}
        onSubmit={(f) => post('request-response', f, f.send_email ? 'Response request emailed' : 'Request recorded')} />
      <RecordResponseDialog rec={dialog.kind === 'respond' ? dialog.rec : null} options={options} busy={busy} onClose={closeDialog}
        onSubmit={(f) => post('responses', f, 'Response recorded')} />
      <DuplicateDialog rec={dialog.kind === 'duplicate' ? dialog.rec : null} options={options} busy={busy} onClose={closeDialog}
        onSubmit={(f) => post('duplicate', f, 'Draft created for the new addressee')} />
      <CloseDialog rec={dialog.kind === 'close' ? dialog.rec : null} options={options} busy={busy} onClose={closeDialog}
        onSubmit={(f) => post('close', f, 'Recommendation closed')} />
      <NoteDialog rec={dialog.kind === 'note' ? dialog.rec : null} busy={busy} onClose={closeDialog}
        onSubmit={(f) => post('notes', f, 'Note added')} />
      <LinkDialog rec={dialog.kind === 'link' ? dialog.rec : null} investigations={investigations} busy={busy} onClose={closeDialog}
        onSubmit={(f) => post('links', f, 'Investigation linked')} />
    </div>
  );
}
